import { useState, useEffect, useMemo } from 'react';
import { useFacility } from '../contexts/FacilityContext';
import FacilityFilter from '../components/FacilityFilter';
import ActiveFacilityBanner from '../components/ActiveFacilityBanner';
import { fetchRevenueStats, fetchProviders } from '../services/api';
import { COMPONENT_LABELS } from '../utils/billing';
import './Revenue.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

function getShortName(name) {
  const parts = (name || '').split(' - ');
  return parts.length > 1 ? parts[parts.length - 1] : name;
}

const fmt = (n) =>
  '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

// ── Pure-SVG Line / Area Chart ────────────────────────────────────────────────
// No external library — all coordinates are computed from the data.
// ViewBox: 500 × 210  Plot area: top=12 right=6 bottom=38 left=6
// Gradient area fills beneath each series give it the "financial dashboard" look.

function LineChart({ labels, billed, collected }) {
  const n = labels.length;

  if (n === 0) return <div className="chart-empty">No data available</div>;

  const VW = 500, VH = 210;
  const PAD = { top: 12, right: 6, bottom: 38, left: 6 };
  const plotW = VW - PAD.left - PAD.right;
  const plotH = VH - PAD.top - PAD.bottom;
  const baseY = PAD.top + plotH; // bottom of the plot area

  const maxVal = Math.max(...billed, ...collected, 1);

  const xOf = (i) =>
    n === 1 ? PAD.left + plotW / 2 : PAD.left + (i / (n - 1)) * plotW;
  const yOf = (v) => PAD.top + (1 - v / maxVal) * plotH;

  // Polyline points string
  const toPoints = (values) =>
    values.map((v, i) => `${xOf(i)},${yOf(v)}`).join(' ');

  // Closed area path: line down to the baseline and back
  const toArea = (values) => {
    const pts = values.map((v, i) => `${xOf(i)},${yOf(v)}`).join(' ');
    return `M ${xOf(0)},${yOf(values[0])} L ${pts.slice(pts.indexOf(' ') + 1)} L ${xOf(n - 1)},${baseY} L ${xOf(0)},${baseY} Z`;
  };

  // X-axis label density: skip labels so they don't overlap (show ~6 max)
  const step = Math.max(1, Math.ceil(n / 6));

  return (
    <svg
      viewBox={`0 0 ${VW} ${VH}`}
      className="line-chart-svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="lcBilledGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#00aeef" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#00aeef" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="lcCollectedGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#05998d" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#05998d" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Horizontal grid lines at 25 / 50 / 75 % */}
      {[0.25, 0.5, 0.75].map((pct) => (
        <line
          key={pct}
          x1={PAD.left} y1={yOf(maxVal * pct)}
          x2={VW - PAD.right} y2={yOf(maxVal * pct)}
          stroke="#f0f0f3" strokeWidth="1"
        />
      ))}
      {/* Baseline */}
      <line
        x1={PAD.left} y1={baseY}
        x2={VW - PAD.right} y2={baseY}
        stroke="#e5e7eb" strokeWidth="1"
      />

      {/* Gradient area fills */}
      <path d={toArea(billed)}    fill="url(#lcBilledGrad)" />
      <path d={toArea(collected)} fill="url(#lcCollectedGrad)" />

      {/* Series lines */}
      <polyline
        points={toPoints(billed)}
        fill="none"
        stroke="#00aeef"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polyline
        points={toPoints(collected)}
        fill="none"
        stroke="#05998d"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Data-point circles with native tooltips */}
      {labels.map((label, i) => (
        <g key={i} className="lc-point-group">
          <circle
            cx={xOf(i)} cy={yOf(billed[i])}
            r="3.5"
            fill="white" stroke="#00aeef" strokeWidth="1.8"
            className="lc-point"
          >
            <title>{`${label}  |  Billed: ${fmt(billed[i])}`}</title>
          </circle>
          <circle
            cx={xOf(i)} cy={yOf(collected[i])}
            r="3.5"
            fill="white" stroke="#05998d" strokeWidth="1.8"
            className="lc-point"
          >
            <title>{`${label}  |  Collected: ${fmt(collected[i])}`}</title>
          </circle>
        </g>
      ))}

      {/* X-axis labels */}
      {labels.map((label, i) =>
        i % step === 0 ? (
          <text
            key={i}
            x={xOf(i)} y={VH - 6}
            textAnchor="middle"
            fontSize="9.5"
            fill="#9ca3af"
            fontFamily="Inter, system-ui, sans-serif"
            fontWeight="500"
          >
            {label}
          </text>
        ) : null
      )}
    </svg>
  );
}

// ── Bar Chart ─────────────────────────────────────────────────────────────────
// Extracted so both chart types share the same props interface.

function BarChart({ labels, billed, collected }) {
  if (labels.length === 0) return <div className="chart-empty">No data available</div>;

  const maxVal = Math.max(...billed, 1);

  return (
    <div className="bar-chart">
      {labels.map((label, i) => (
        <div key={label} className="bar-group">
          <div className="bar-pair">
            <div
              className="bar bar-billed"
              style={{ height: `${(billed[i] / maxVal) * 100}%` }}
              title={`Billed: ${fmt(billed[i])}`}
            />
            <div
              className="bar bar-collected"
              style={{ height: `${(collected[i] / maxVal) * 100}%` }}
              title={`Collected: ${fmt(collected[i])}`}
            />
          </div>
          <span className="bar-label">{label}</span>
        </div>
      ))}
    </div>
  );
}

// ── Icons ─────────────────────────────────────────────────────────────────────

const BarIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="2"  y="10" width="5" height="12" rx="1.5" />
    <rect x="9"  y="6"  width="5" height="16" rx="1.5" />
    <rect x="16" y="2"  width="5" height="20" rx="1.5" />
  </svg>
);

const LineIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="2,18 8,10 13,14 22,4" />
  </svg>
);

// ── Main Component ────────────────────────────────────────────────────────────

function Revenue() {
  const { selectedFacility, facilities } = useFacility();
  const [manualFilter, setManualFilter] = useState([]);

  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);

  // Provider filter for the provider revenue card
  const [providerList,   setProviderList]   = useState([]);
  const [providerFilter, setProviderFilter] = useState('');  // '' = all, else provider id string

  // Chart controls
  const [interval,  setInterval]  = useState('monthly');  // 'weekly' | 'monthly' | 'yearly'
  const [chartType, setChartType] = useState('bar');       // 'bar' | 'line'

  const facilityId  = selectedFacility?.id ?? null;
  const facilityIds = !selectedFacility && manualFilter.length > 0 ? manualFilter : null;

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchRevenueStats(facilityId, facilityIds)
      .then((res) => setRevenueData(res.data))
      .catch(() => setError('Failed to load revenue data. Please try again.'))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, manualFilter.join(',')]);

  useEffect(() => {
    fetchProviders().then((res) => setProviderList(res.data || [])).catch(() => {});
  }, []);

  // ── Derived values ──────────────────────────────────────────────────────────

  const kpi = revenueData?.kpi ?? {
    totalBilled: 0, totalCollected: 0, totalOutstanding: 0,
    insuranceOutstanding: 0, patientOutstanding: 0,
    totalDenied: 0, claimCount: 0, collectionRate: 0,
    creditBalanceOpen: 0, creditBalancePct: 0,
  };

  // Build { labels, billed, collected } for whichever interval is selected
  const currentChartData = useMemo(() => {
    if (!revenueData) return { labels: [], billed: [], collected: [] };

    if (interval === 'weekly') {
      const rows = revenueData.weekly ?? [];
      return {
        labels:    rows.map((r) => {
          // wk_start is "YYYY-MM-DD"; append time to neutralise timezone offsets
          const d = new Date(`${r.weekStart}T00:00:00`);
          return d.toLocaleString('default', { month: 'short', day: 'numeric' });
        }),
        billed:    rows.map((r) => r.billed),
        collected: rows.map((r) => r.collected),
      };
    }

    if (interval === 'yearly') {
      const rows = revenueData.yearly ?? [];
      return {
        labels:    rows.map((r) => String(r.year)),
        billed:    rows.map((r) => r.billed),
        collected: rows.map((r) => r.collected),
      };
    }

    // Monthly (default)
    const rows = revenueData.monthly ?? [];
    return {
      labels: rows.map(({ month }) => {
        const [y, mo] = month.split('-');
        return new Date(y, parseInt(mo) - 1).toLocaleString('default', { month: 'short', year: '2-digit' });
      }),
      billed:    rows.map((r) => r.billed),
      collected: rows.map((r) => r.collected),
    };
  }, [revenueData, interval]);

  // Donut: { paid: amount, ... }
  const statusDist = useMemo(() => {
    const dist = { paid: 0, submitted: 0, pending: 0, denied: 0 };
    (revenueData?.byStatus ?? []).forEach(({ status, amount }) => {
      if (status in dist) dist[status] = amount;
    });
    return dist;
  }, [revenueData]);

  const facilityBreakdown = useMemo(() =>
    (revenueData?.byFacility ?? []).map((f) => ({ ...f, shortName: getShortName(f.name) })),
    [revenueData]
  );

  const maxFacility = Math.max(...facilityBreakdown.map((f) => f.billed), 1);

  const providerBreakdown = useMemo(() => {
    const all = revenueData?.byProvider ?? [];
    if (!providerFilter) return all;
    return all.filter((p) => String(p.providerId) === providerFilter);
  }, [revenueData, providerFilter]);

  const maxProvider = Math.max(...providerBreakdown.map((p) => p.billed), 1);

  // Billed vs collected per billing component. Only worth showing once a facility actually
  // splits its billing — a single-component practice would just see one bar restating the
  // totals above it.
  const componentBreakdown = useMemo(
    () => (revenueData?.byComponent ?? []).filter((c) => c.claimCount > 0),
    [revenueData]
  );
  const maxComponent = Math.max(...componentBreakdown.map((c) => c.billed), 1);
  const showComponentBreakdown = componentBreakdown.length > 1;
  const hasTechnicalRevenue = componentBreakdown.some((c) => c.component === 'technical');
  const totalStatusAmount = Object.values(statusDist).reduce((s, v) => s + v, 0);

  const donutSegments = useMemo(() => {
    const colors = {
      paid: '#05998d', submitted: '#3b82f6',
      pending: '#f59e0b', denied: '#ef4444',
    };
    const total = totalStatusAmount || 1;
    let offset = 0;
    return Object.entries(statusDist)
      .map(([status, amount]) => {
        const pct = (amount / total) * 100;
        const seg = { status, amount, pct, color: colors[status], offset };
        offset += pct;
        return seg;
      })
      .filter((s) => s.pct > 0);
  }, [statusDist, totalStatusAmount]);

  // Interval label for the chart description
  const intervalDesc = interval === 'weekly'
    ? 'Last 13 weeks'
    : interval === 'yearly'
      ? 'Year over year'
      : 'Month over month';

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="revenue-page">
      <ActiveFacilityBanner />

      <div className="revenue-header">
        <div>
          <h1 className="revenue-title">Revenue</h1>
          <p className="revenue-subtitle">
            {selectedFacility
              ? `Financial analytics for ${selectedFacility.name}`
              : 'Financial analytics across all facilities'}
          </p>
        </div>
        {!selectedFacility && (
          <FacilityFilter
            selectedIds={manualFilter}
            onChange={setManualFilter}
            facilities={facilities}
          />
        )}
      </div>

      {error && <div className="revenue-error">{error}</div>}

      {/* ── KPI Cards ── */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-icon kpi-icon-billed">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Total Billed</span>
            <span className="kpi-value">{loading ? '—' : fmt(kpi.totalBilled)}</span>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon kpi-icon-collected">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Collected</span>
            <span className="kpi-value">{loading ? '—' : fmt(kpi.totalCollected)}</span>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon kpi-icon-ins-outstanding">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <line x1="2" y1="10" x2="22" y2="10" />
            </svg>
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Ins. Outstanding</span>
            <span
              className="kpi-value"
              title={loading ? undefined : 'Insurance billed A/R: claims submitted to a payer (not yet-to-send pending/generated claims), excluding the patient-responsibility portion (shown as Patient Outstanding). Equals the AR page total minus patient balances.'}
            >
              {loading ? '—' : fmt(kpi.insuranceOutstanding)}
            </span>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon kpi-icon-pat-outstanding">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Patient Outstanding</span>
            <span
              className="kpi-value"
              title={loading ? undefined : 'All patient-owed balances: remit patient-responsibility across active claims + self-pay (uninsured) charges not yet billed to insurance, minus patient payments already collected. Broader than the AR-vs-Ins gap, which is only the patient-resp on submitted/denied claims.'}
            >
              {loading ? '—' : fmt(kpi.patientOutstanding)}
            </span>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon kpi-icon-rate">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Collection Rate</span>
            <span className="kpi-value">{loading ? '—' : `${kpi.collectionRate.toFixed(1)}%`}</span>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon kpi-icon-credit">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
              <polyline points="17 6 23 6 23 12" />
            </svg>
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Credit Balance %</span>
            <span
              className="kpi-value"
              title={loading ? undefined : `${fmt(kpi.creditBalanceOpen)} open — owed back to payers`}
            >
              {loading ? '—' : `${(kpi.creditBalancePct ?? 0).toFixed(1)}%`}
            </span>
          </div>
        </div>
      </div>

      {/* ── Charts Row ── */}
      <div className="charts-row">

        {/* Revenue Trend — interval + type controls */}
        <div className="chart-card chart-card-wide">
          <div className="chart-card-header">
            <div>
              <h3 className="chart-title">Revenue Trend</h3>
              <p className="chart-desc">{intervalDesc} — billed vs. collected</p>
            </div>

            <div className="chart-controls">
              {/* Interval pills */}
              <div className="interval-pills" role="group" aria-label="Time interval">
                {['weekly', 'monthly', 'yearly'].map((iv) => (
                  <button
                    key={iv}
                    className={`interval-pill${interval === iv ? ' active' : ''}`}
                    onClick={() => setInterval(iv)}
                  >
                    {iv.charAt(0).toUpperCase() + iv.slice(1)}
                  </button>
                ))}
              </div>

              {/* Chart type toggle */}
              <div className="chart-type-toggle" role="group" aria-label="Chart type">
                <button
                  className={`chart-type-btn${chartType === 'bar' ? ' active' : ''}`}
                  onClick={() => setChartType('bar')}
                  title="Bar chart"
                >
                  <BarIcon />
                </button>
                <button
                  className={`chart-type-btn${chartType === 'line' ? ' active' : ''}`}
                  onClick={() => setChartType('line')}
                  title="Line chart"
                >
                  <LineIcon />
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="chart-loading">Loading…</div>
          ) : chartType === 'line' ? (
            <LineChart
              labels={currentChartData.labels}
              billed={currentChartData.billed}
              collected={currentChartData.collected}
            />
          ) : (
            <BarChart
              labels={currentChartData.labels}
              billed={currentChartData.billed}
              collected={currentChartData.collected}
            />
          )}

          <div className="chart-legend">
            <span className="legend-item"><span className="legend-dot legend-billed" />Billed</span>
            <span className="legend-item"><span className="legend-dot legend-collected" />Collected</span>
          </div>
        </div>

        {/* Claim Status Donut */}
        <div className="chart-card">
          <h3 className="chart-title">Claim Status</h3>
          <p className="chart-desc">Distribution by dollar amount</p>
          {loading ? (
            <div className="chart-loading">Loading…</div>
          ) : (
            <>
              <div className="donut-wrapper">
                <svg viewBox="0 0 42 42" className="donut-svg">
                  <circle cx="21" cy="21" r="15.915" fill="transparent" stroke="#f3f4f6" strokeWidth="5" />
                  {donutSegments.map((seg) => (
                    <circle
                      key={seg.status}
                      cx="21" cy="21" r="15.915"
                      fill="transparent"
                      stroke={seg.color}
                      strokeWidth="5"
                      strokeDasharray={`${seg.pct} ${100 - seg.pct}`}
                      strokeDashoffset={`${25 - seg.offset}`}
                      strokeLinecap="round"
                    />
                  ))}
                </svg>
                <div className="donut-center">
                  <span className="donut-total">{kpi.claimCount}</span>
                  <span className="donut-label">Claims</span>
                </div>
              </div>
              <div className="donut-legend">
                {donutSegments.map((seg) => (
                  <div key={seg.status} className="donut-legend-item">
                    <span className="legend-dot" style={{ background: seg.color }} />
                    <span className="donut-legend-status">{seg.status}</span>
                    <span className="donut-legend-value">{fmt(seg.amount)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Revenue by Facility — hidden when a specific facility is active ── */}
      {!selectedFacility && (
        <div className="chart-card">
          <h3 className="chart-title">Revenue by Facility</h3>
          <p className="chart-desc">Total billed and collected per facility</p>
          {loading ? (
            <div className="chart-loading">Loading…</div>
          ) : facilityBreakdown.length === 0 ? (
            <div className="chart-empty">No facility data available</div>
          ) : (
            <div className="facility-bars">
              {facilityBreakdown.map((fac) => (
                <div key={fac.id} className="facility-bar-row">
                  <span className="facility-bar-name">{fac.shortName}</span>
                  <div className="facility-bar-track">
                    <div
                      className="facility-bar-fill facility-bar-billed"
                      style={{ width: `${(fac.billed / maxFacility) * 100}%` }}
                    />
                    <div
                      className="facility-bar-fill facility-bar-collected"
                      style={{ width: `${(fac.collected / maxFacility) * 100}%` }}
                    />
                  </div>
                  <div className="facility-bar-values">
                    <span className="facility-bar-amount">{fmt(fac.billed)}</span>
                    <span className="facility-bar-collected-amt">{fmt(fac.collected)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="chart-legend" style={{ marginTop: '16px' }}>
            <span className="legend-item"><span className="legend-dot legend-billed" />Billed</span>
            <span className="legend-item"><span className="legend-dot legend-collected" />Collected</span>
          </div>
        </div>
      )}

      {/* ── Revenue by Provider ── */}
      <div className="chart-card">
        <div className="chart-card-header">
          <div>
            <h3 className="chart-title">Revenue by Provider</h3>
            <p className="chart-desc">
              {providerFilter
                ? `Showing ${providerBreakdown[0]?.providerName ?? 'selected provider'}`
                : 'Billed and collected per rendering provider'}
            </p>
          </div>
          <div className="provider-filter-wrap">
            <svg className="provider-filter-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
            </svg>
            <select
              className="provider-filter-select"
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value)}
            >
              <option value="">All Providers</option>
              {providerList.map((p) => (
                <option key={p.id} value={String(p.id)}>{p.name}</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="chart-loading">Loading…</div>
        ) : providerBreakdown.length === 0 ? (
          <div className="chart-empty">
            {providerFilter
              ? 'No claims linked to this provider yet'
              : 'No provider-linked claims yet — select a provider when creating claims to track revenue here'}
          </div>
        ) : providerFilter && providerBreakdown.length === 1 ? (
          // Single-provider detail view
          <div className="provider-detail">
            <div className="provider-detail-stats">
              <div className="provider-stat">
                <span className="provider-stat-label">Total Billed</span>
                <span className="provider-stat-value">{fmt(providerBreakdown[0].billed)}</span>
              </div>
              <div className="provider-stat">
                <span className="provider-stat-label">Collected</span>
                <span className="provider-stat-value provider-stat-collected">{fmt(providerBreakdown[0].collected)}</span>
              </div>
              <div className="provider-stat">
                <span className="provider-stat-label">Collection Rate</span>
                <span className="provider-stat-value">
                  {providerBreakdown[0].billed > 0
                    ? `${((providerBreakdown[0].collected / providerBreakdown[0].billed) * 100).toFixed(1)}%`
                    : '—'}
                </span>
              </div>
              <div className="provider-stat">
                <span className="provider-stat-label">Claims</span>
                <span className="provider-stat-value">{providerBreakdown[0].claimCount}</span>
              </div>
              <div className="provider-stat">
                <span className="provider-stat-label">Denials</span>
                <span className="provider-stat-value provider-stat-denied">{providerBreakdown[0].deniedCount}</span>
              </div>
            </div>
          </div>
        ) : (
          // Multi-provider bar chart
          <div className="facility-bars">
            {providerBreakdown.map((prov) => (
              <div key={prov.providerId} className="facility-bar-row">
                <span className="facility-bar-name">{prov.providerName}</span>
                <div className="facility-bar-track">
                  <div
                    className="facility-bar-fill facility-bar-billed"
                    style={{ width: `${(prov.billed / maxProvider) * 100}%` }}
                  />
                  <div
                    className="facility-bar-fill facility-bar-collected"
                    style={{ width: `${(prov.collected / maxProvider) * 100}%` }}
                  />
                </div>
                <div className="facility-bar-values">
                  <span className="facility-bar-amount">{fmt(prov.billed)}</span>
                  <span className="facility-bar-collected-amt">{fmt(prov.collected)}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {providerBreakdown.length > 0 && !(providerFilter && providerBreakdown.length === 1) && (
          <div className="chart-legend" style={{ marginTop: '16px' }}>
            <span className="legend-item"><span className="legend-dot legend-billed" />Billed</span>
            <span className="legend-item"><span className="legend-dot legend-collected" />Collected</span>
          </div>
        )}
      </div>

      {/* ── Revenue by Billing Component ── */}
      {showComponentBreakdown && (
        <div className="chart-card">
          <div className="chart-card-header">
            <div>
              <h3 className="chart-title">Revenue by Billing Component</h3>
              <p className="chart-desc">
                Billed and collected per component — the technical half (TC) and the
                professional half (26) of a split study are paid at very different rates
              </p>
            </div>
          </div>

          {loading ? (
            <div className="chart-loading">Loading…</div>
          ) : (
            <div className="facility-bars">
              {componentBreakdown.map((comp) => (
                <div key={comp.component} className="facility-bar-row">
                  <span className="facility-bar-name">
                    {COMPONENT_LABELS[comp.component] || comp.component}
                  </span>
                  <div className="facility-bar-track">
                    <div
                      className="facility-bar-fill facility-bar-billed"
                      style={{ width: `${(comp.billed / maxComponent) * 100}%` }}
                    />
                    <div
                      className="facility-bar-fill facility-bar-collected"
                      style={{ width: `${(comp.collected / maxComponent) * 100}%` }}
                    />
                  </div>
                  <div className="facility-bar-values">
                    <span className="facility-bar-amount">{fmt(comp.billed)}</span>
                    <span className="facility-bar-collected-amt">{fmt(comp.collected)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Expectation-setting, not a defect: CMS and most commercial payers apply the
              Multiple Procedure Payment Reduction to the technical component, so the second
              and subsequent imaging studies in one session pay TC at 50%. A technical
              collection rate below the professional one is the normal shape of this data. */}
          {hasTechnicalRevenue && (
            <p className="chart-footnote">
              <strong>Expect a lower technical collection rate on multi-study encounters.</strong>{' '}
              The Multiple Procedure Payment Reduction (MPPR) pays the technical component of
              each subsequent same-session imaging study at 50%. Billed-vs-collected variance
              on those encounters is by design, not a denial pattern.
            </p>
          )}

          <div className="chart-legend" style={{ marginTop: '16px' }}>
            <span className="legend-item"><span className="legend-dot legend-billed" />Billed</span>
            <span className="legend-item"><span className="legend-dot legend-collected" />Collected</span>
          </div>
        </div>
      )}

    </div>
  );
}

export default Revenue;
